
import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";

import { Autocomplete, Button, TextField, Typography } from "@mui/material";
import { Stack } from "@mui/system";

import { useAuthContext } from "src/hooks/auth/use-auth-context";

import { useTranslate } from "src/locales";
import { useGetCompanyContactLinksQuery } from "src/services/connections/connections.service";
import { useAddContactLinkMutation, useGetOrganizationContactsQuery } from "src/services/contacts/contacts.service";
import { ContactLinkEntityType, Contact } from "src/services/contacts/contacts.types";

import { ConfirmDialog } from "src/components/custom-dialog";
import Iconify from "src/components/iconify";
import { EntityLinkType } from "src/services/common.types";

type Props = {
  open: boolean;
  onClose: () => void;
  onAddContact: () => void;
  profileId: string;
}

const DEFAULT_PAGE_OPTIONS = {
  page: 1,
  per_page: 5
};

export const ContactEditorDialog = ({
  open,
  onClose,
  onAddContact,
  profileId
}: Props) => {

  const { t } = useTranslate();

  const authContext = useAuthContext();

  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const [addContactLink, { isLoading: isAddingContactLinks }] = useAddContactLinkMutation();

  const { data: contactLinks, isLoading: isLoadingLinks } = useGetCompanyContactLinksQuery({
    profile_id: profileId,
    params: DEFAULT_PAGE_OPTIONS
  }, {
    skip: !profileId,
  });

  const { currentData: contacts, isLoading: isContactsLoading } = useGetOrganizationContactsQuery(
    {
      org_id: authContext.organization.id,
      page: DEFAULT_PAGE_OPTIONS,
      search_query: contactSearchQuery
    }
  );

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setContactSearchQuery(value);
    }, 500),
    [setContactSearchQuery]
  );

  const onLinkModalClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const selectableContacts = useMemo(() => {
    if (!contacts) return [];

    const linkedContactIds = contactLinks?.results.map((contact) => contact.contact.id) ?? [];

    return contacts.results.filter((contact) => !linkedContactIds.includes(contact.id));
  }, [contacts, contactLinks]);

  const handleUpdateContactLinks = useCallback(async () => {

    if (!selectedContact) return;

    try {
      await addContactLink({
        data: {
          entity_id: profileId,
          entity_type: ContactLinkEntityType.COMPANY_PROFILE,
          contacts: [{
            type: EntityLinkType.OTHER,
            id: selectedContact.id,
          }]
        }
      }).unwrap();

      onLinkModalClose();

      enqueueSnackbar(t('companies.profile.api.add_contact_link.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.add_contact_link.default_error'), { variant: 'error' });
    }
  }, [onLinkModalClose, profileId, selectedContact, addContactLink, t]);

  return (
    <ConfirmDialog
      open={open}
      title={
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h6">
            {t('companies.profile.linked_contacts.editor_title')}
          </Typography>

          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={onAddContact}
          >
            {t('common.create')}
          </Button>

        </Stack>
      }
      onClose={onLinkModalClose}
      content={
        <Autocomplete
          sx={{
            paddingTop: 2,
          }}
          multiple={false}
          options={selectableContacts as Contact[] ?? []}
          value={selectedContact as Contact}
          onInputChange={(_e, value) => onSearchQueryChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(
                'companies.profile.primary_contact.search'
              )}
              placeholder={t(
                'companies.profile.primary_contact.search_placeholder'
              )}
            />
          )}
          onChange={(_e, value: Contact | null) => setSelectedContact(value)}
          renderOption={({ key, ...rest }, option: Contact) => (
            <li key={key} {...rest}>
              <Stack direction="column">
                <Typography variant="body1">
                  {`${option.first_name} ${option.last_name}`}
                </Typography>
              </Stack>
            </li>
          )}
          getOptionLabel={(option) => `${option.first_name} ${option.last_name}` as string}
          isOptionEqualToValue={(option, value) =>
            option && (option as Contact).id === (value as Contact).id
          }
          getOptionKey={(option) => (option as Contact).id as string}
        />
      }
      action={
        <Button variant="contained" disabled={!selectedContact} onClick={handleUpdateContactLinks}>
          {t('common.link')}
        </Button>
      }
    />
  )
}